import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth-store';
import { useGamificationStore } from '../../stores/gamification-store';
import { useRumioBalance } from '../../pages/public/deals/hooks/useRumios';
import ProfileBadge from '../common/ProfileBadge';
import ProfileMenu from '../common/ProfileMenu';
import AnimatedRumioIcon from '../common/AnimatedRumioIcon';
import { useWebsiteContentOptional } from '../../contexts/WebsiteContentContext';
import { useLanguage } from '../../contexts/LanguageContext';

type NavLink = {
  name: string;
  href: string;
  onClick?: () => void;
};

export default function Header() {
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated } = useAuthStore();
  const { xp, level, init: initGamification } = useGamificationStore();
  const { data: rumioBalance } = useRumioBalance();
  const content = useWebsiteContentOptional();
  const { t } = useLanguage();

  // Initialize gamification store when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      initGamification();
    }
  }, [isAuthenticated, initGamification]);

  // Helper to get translated content with fallback
  const get = (key: string, fallback: string) =>
    content?.getContent('nav', key, fallback) ?? fallback;

  const isLandingPage = location.pathname === '/';

  // Landing page anchor links (only shown on landing page)
  const landingNavigation = [
    { name: get('features', 'Features'), href: '/#features' },
    { name: get('pricing', 'Pricing'), href: '/#pricing' },
    { name: get('faq', 'FAQ'), href: '/#faq' },
    { name: get('support', 'Support'), href: '#', onClick: () => window.Axiss?.open('contact') },
  ];

  return (
    <header className="bg-white/95 backdrop-blur-sm sticky top-0 z-50 border-b border-gray-100" role="banner">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg">
        {get('skip_to_content', 'Skip to main content')}
      </a>
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" aria-label={t('header.mainNavigation', 'Main navigation')}>
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center" aria-label={t('header.goToHomepage', 'RUMIO - Go to homepage')}>
              <img src="/logo.svg" alt="RUMIO" className="h-8" />
            </Link>
          </div>

          {/* Desktop navigation - only anchor links on landing page */}
          {isLandingPage && (
            <div className="hidden md:flex md:items-center md:gap-8">
              {landingNavigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={'onClick' in item ? (e: React.MouseEvent) => { e.preventDefault(); (item as NavLink).onClick!(); } : undefined}
                  className="text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors"
                >
                  {item.name}
                </a>
              ))}
            </div>
          )}

          {/* Rumio/XP indicators + Profile badge */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Rumio Balance - only show when authenticated */}
            {isAuthenticated && (
              <Link
                to="/deals"
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors"
                title={t('header.rumioBalance', 'Your Rumio balance')}
              >
                <AnimatedRumioIcon balance={rumioBalance?.balance ?? 0} size="md" />
                <span className="text-sm font-semibold text-amber-700">
                  {rumioBalance?.balance ?? 0}
                </span>
              </Link>
            )}

            {/* XP/Level indicator - only show when authenticated */}
            {isAuthenticated && (
              <Link
                to="/account/profile"
                className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-lg bg-primary-50 hover:bg-primary-100 transition-colors"
                title={t('header.levelXp', 'Level {{level}} - {{xp}} XP', { level, xp })}
              >
                <span className="text-sm">⭐</span>
                <span className="text-sm font-semibold text-primary-700">
                  {t('header.levelShort', 'Lv{{level}}', { level })}
                </span>
              </Link>
            )}

            <ProfileBadge onClick={() => setProfileMenuOpen(true)} />
          </div>

          {/* Profile Menu */}
          <ProfileMenu
            isOpen={profileMenuOpen}
            onClose={() => setProfileMenuOpen(false)}
          />
        </div>
      </nav>
    </header>
  );
}
